import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const CompanyAdminAuthContext = createContext(null);

const TOKEN_KEY   = 'company_admin_token';
const COMPANY_KEY = 'company_admin_company';
const USER_KEY    = 'company_admin_user';
const PERMS_KEY   = 'company_admin_permissions';

const API = process.env.REACT_APP_BACKEND_URL;

export const CompanyAdminAuthProvider = ({ children }) => {
  const [company, setCompany]         = useState(null);
  const [user, setUser]               = useState(null);
  const [token, setToken]             = useState(null);
  const [loading, setLoading]         = useState(true);
  const [userPermissions, setUserPermissions] = useState(null); // null = not loaded yet
  const [companyConfig, setCompanyConfig] = useState(null);

  // Fetch company config (ai_chat, allow_premium_plan, etc.)
  const fetchCompanyConfig = async (authToken) => {
    if (!authToken) return;
    try {
      const res = await axios.get(
        `${API}/api/v1/admin/company/config`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const cfg = res.data || {};
      setCompanyConfig(cfg);
      localStorage.setItem('company_admin_config', JSON.stringify(cfg));
    } catch {
      setCompanyConfig({ ai_chat: 'enabled' });
    }
  };

  // Fetch permissions for the current user (managers / agents only)
  const fetchPermissions = useCallback(async (userId, authToken) => {
    if (!userId || !authToken) { setUserPermissions({}); return; }
    try {
      const res = await axios.get(
        `${API}/api/v1/admin/company/users/${userId}/permissions`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const perms = res.data?.permissions || {};
      setUserPermissions(perms);
      localStorage.setItem(PERMS_KEY, JSON.stringify(perms));
    } catch {
      setUserPermissions({});
    }
  }, []);

  useEffect(() => {
    const storedToken   = localStorage.getItem(TOKEN_KEY);
    const storedCompany = localStorage.getItem(COMPANY_KEY);
    const storedUser    = localStorage.getItem(USER_KEY);
    const storedPerms   = localStorage.getItem(PERMS_KEY);
    const storedConfig  = localStorage.getItem('company_admin_config');

    if (storedToken && storedCompany) {
      setToken(storedToken);
      try { setCompany(JSON.parse(storedCompany)); } catch {}
      if (storedConfig) { try { setCompanyConfig(JSON.parse(storedConfig)); } catch {} }
      fetchCompanyConfig(storedToken);
      let parsedUser = null;
      if (storedUser) { try { parsedUser = JSON.parse(storedUser); setUser(parsedUser); } catch {} }

      // Admin (type 1) bypasses permissions; managers/agents need them fetched
      const utype = Number(parsedUser?.user_type);
      if (utype === 1) {
        setUserPermissions(null); // null = admin = unrestricted
      } else if (storedPerms) {
        try { setUserPermissions(JSON.parse(storedPerms)); } catch { setUserPermissions({}); }
        // Also refresh from server in background
        if (parsedUser?.user_id) fetchPermissions(parsedUser.user_id, storedToken);
      } else if (parsedUser?.user_id) {
        fetchPermissions(parsedUser.user_id, storedToken);
      } else {
        setUserPermissions({});
      }
    }
    setLoading(false);
  }, []); // eslint-disable-line

  const login = (companyData, accessToken, userData = null) => {
    setCompany(companyData);
    setUser(userData);
    setToken(accessToken);
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(COMPANY_KEY, JSON.stringify(companyData));
    fetchCompanyConfig(accessToken);
    if (userData) localStorage.setItem(USER_KEY, JSON.stringify(userData));
    else localStorage.removeItem(USER_KEY);

    const utype = Number(userData?.user_type);
    if (utype === 1) {
      setUserPermissions(null); // admin = no restrictions
      localStorage.removeItem(PERMS_KEY);
    } else if (userData?.user_id) {
      fetchPermissions(userData.user_id, accessToken);
    } else {
      setUserPermissions({});
    }
  };

  const logout = () => {
    setCompany(null); setUser(null); setToken(null); setUserPermissions(null); setCompanyConfig(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(COMPANY_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(PERMS_KEY);
    localStorage.removeItem('company_admin_config');
  };

  // Helper: null = admin (all allowed); otherwise check key
  const hasPermission = useCallback((key) => {
    if (userPermissions === null) return true; // admin
    return !!userPermissions[key]?.visible;
  }, [userPermissions]);

  // Helper: check action-level permission (add/edit/delete)
  const canAction = useCallback((key, action) => {
    if (userPermissions === null) return true; // admin — all actions allowed
    return !!userPermissions[key]?.[action];
  }, [userPermissions]);

  return (
    <CompanyAdminAuthContext.Provider value={{
      company, user, token, login, logout,
      isAuthenticated: !!token,
      loading,
      userPermissions,
      hasPermission,
      canAction,
      companyConfig,
      refreshPermissions: () => user?.user_id && token ? fetchPermissions(user.user_id, token) : Promise.resolve(),
    }}>
      {children}
    </CompanyAdminAuthContext.Provider>
  );
};

export const useCompanyAdminAuth = () => {
  const ctx = useContext(CompanyAdminAuthContext);
  if (!ctx) throw new Error('useCompanyAdminAuth must be used within CompanyAdminAuthProvider');
  return ctx;
};
